import { createFileRoute, useParams, useRouter } from '@tanstack/react-router';
import { ArrowLeftIcon } from 'lucide-react';
import type { JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { ReportViewer } from '@/features/insights/ReportViewer.js';

// In-app insights report viewer. Lives at the SPA path `/reports/:runId` (a
// trailing-underscore route name so it does NOT nest under the `/reports`
// ComingSoon page). The route deliberately sits OUTSIDE every edge-proxied
// prefix (`/api /v1 /ext /quality /sync /usage /scheduler /insights`): those are
// routed to backend services by the edge (Caddy / Front Door), so a hard reload
// or deep-link there would never reach the SPA. `/reports/*` is free → the edge
// falls through to the static SPA, the route resolves, and ReportViewer fetches
// the report from the API path `/v1/insights/report/:runId` (unchanged backend
// contract) with the platform JWT, rendering the HTML in a sandboxed iframe.
export const Route = createFileRoute('/reports_/$runId')({
  component: ReportPage,
});

/**
 * The report fills the window: `AppShell` treats `/reports/:runId` as a bare route, so the sidebar
 * and the global header stay out of the way of a page that is one document. `Back` returns wherever
 * the reader came from — a chat, the automation's page, or a results feed — because the report is
 * reachable from all three and none of them is the right hardcoded destination. The viewer offers no
 * full-screen control here: this IS it.
 */
function ReportPage(): JSX.Element {
  const { runId } = useParams({ from: '/reports_/$runId' });
  const router = useRouter();

  return (
    <div className="flex h-dvh w-full flex-col">
      <header className="flex shrink-0 items-center border-b border-border/40 bg-background px-4 py-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            router.history.back();
          }}
        >
          <ArrowLeftIcon className="size-3.5" strokeWidth={1.75} />
          Back
        </Button>
      </header>
      {/* 832px is the width the report gets everywhere it is embedded — the chat bubble, the
          results feed, the public results page. The template lays itself out in a 760px column and
          paints its hero and page ground full-bleed, so this leaves the same margin of ground on
          each side; at exactly 760 the report reads as if its edges were cut off. */}
      <div className="min-h-0 flex-1 bg-muted/30">
        <div className="mx-auto h-full w-full max-w-[832px] bg-background shadow-sm">
          <ReportViewer runId={runId} offerFullScreen={false} />
        </div>
      </div>
    </div>
  );
}
